import { liveApi } from '@features/live/api/liveApi';
import { IEventInfoLine, ILiveSport } from '@features/live/api/liveApi.type';
import { ILiveSliceState, ILiveStamp } from '@features/live/slice/liveSlice.type';
import { SNAPSHOT_MAX_AGE_MS } from '@features/live/websocket/socketPolicy';
import { sortLiveEvents } from '@features/live/slice/liveSliceSelectors';
import {
  createLiveSync,
  isNewer,
  markLive,
  mergeLiveEvent,
  mergeLiveList,
  pruneLiveSync,
  requestSince,
} from '@features/live/slice/liveSync';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

const initialState: ILiveSliceState = {
  tabs: [],
  events: [],
  sports: [],
  favorites: [],
  favoritesSyncedAt: 0,
  showFavorites: false,
  topEventIds: [],
  sync: createLiveSync(),
};
type StampedAction<P> = PayloadAction<P, string, ILiveStamp>;
const stamped = <P>(payload: P) => ({ payload, meta: { at: Date.now() } });
const hideEvent = (state: Draft<ILiveSliceState>, eventId: number) => {
  const index = state.events.findIndex((event) => event.id === eventId);
  if (index !== -1) {
    state.events[index].show = false;
    const tabIndex = state.tabs.findIndex((tab) => tab.eventId === eventId);
    if (tabIndex !== -1) {
      state.tabs.splice(tabIndex, 1);
    }
  }
};
const liveSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLiveSports: (state, action: PayloadAction<ILiveSport[]>) => {
      state.sports = action.payload;
    },
    setLiveEventRates: {
      reducer: (state, action: StampedAction<{
        id: number,
        count: number,
        mainline?: Record<string, string>,
        additional: Record<string, string>
      }>) => {
        const { id, additional, count, mainline } = action.payload;
        markLive(state.sync, [ id ], additional ? [ 'odds', 'extra' ] : [ 'odds' ], action.meta.at);
        const index = state.events.findIndex((event) => event.id === id);
        if (index !== -1) {
          state.events[index].count = count;
          if (additional) {
            state.events[index].additional = additional;
          }
          if (mainline) {
            state.events[index].mainlines = mainline;
          }
          state.events = sortLiveEvents(state.events);
        }
      },
      prepare: (rates: {
        id: number,
        count: number,
        mainline?: Record<string, string>,
        additional: Record<string, string>
      }) => stamped(rates),
    },
    setLiveEventInfoLine: {
      reducer: (state, action: StampedAction<{ id: number, data: IEventInfoLine }>) => {
        const { id, data } = action.payload;
        markLive(state.sync, [ id ], [ 'info' ], action.meta.at);
        const index = state.events.findIndex((event) => event.id === id);
        if (index !== -1) {
          state.events[index].infoLine = data;
        }
      },
      prepare: (info: { id: number, data: IEventInfoLine }) => stamped(info),
    },
    showLiveEvent: {
      reducer: (state, action: StampedAction<number>) => {
        markLive(state.sync, [ action.payload ], [ 'show' ], action.meta.at);
        const index = state.events.findIndex((event) => event.id === action.payload);
        if (index !== -1) {
          state.events[index].show = true;
        }
        state.events = sortLiveEvents(state.events);
      },
      prepare: (eventId: number) => stamped(eventId),
    },
    hideLiveEvent: {
      reducer: (state, action: StampedAction<number>) => {
        markLive(state.sync, [ action.payload ], [ 'show' ], action.meta.at);
        hideEvent(state, action.payload);
      },
      prepare: (eventId: number) => stamped(eventId),
    },
    lockLiveEvent: {
      reducer: (state, action: StampedAction<number>) => {
        markLive(state.sync, [ action.payload ], [ 'lock' ], action.meta.at);
        const index = state.events.findIndex((event) => event.id === action.payload);
        if (index !== -1) {
          state.events[index].lock = true;
        }
      },
      prepare: (eventId: number) => stamped(eventId),
    },
    unlockLiveEvent: {
      reducer: (state, action: StampedAction<number>) => {
        markLive(state.sync, [ action.payload ], [ 'lock' ], action.meta.at);
        const index = state.events.findIndex((event) => event.id === action.payload);
        if (index !== -1) {
          state.events[index].lock = false;
        }
      },
      prepare: (eventId: number) => stamped(eventId),
    },
    lockAllLiveEvent: {
      reducer: (state, action: StampedAction<undefined>) => {
        markLive(state.sync, state.events.map((event) => event.id), [ 'lock' ], action.meta.at);
        state.events.forEach((event) => {
          event.lock = true;
        });
      },
      prepare: () => stamped(undefined),
    },
    unlockAllLiveEvent: {
      reducer: (state, action: StampedAction<undefined>) => {
        markLive(state.sync, state.events.map((event) => event.id), [ 'lock' ], action.meta.at);
        state.events.forEach((event) => {
          event.lock = false;
        });
      },
      prepare: () => stamped(undefined),
    },
    lockAllScout: {
      reducer: (state, action: StampedAction<number[]>) => {
        if (Array.isArray(action.payload)) {
          markLive(state.sync, action.payload, [ 'lock' ], action.meta.at);
          state.events.forEach((event) => {
            if (action.payload.includes(event.id)) {
              event.lock = true;
            }
          });
        }
      },
      prepare: (eventIds: number[]) => stamped(eventIds),
    },
    addLiveEventTab: (state, action: PayloadAction<number>) => {
      const eventExists = state.tabs.findIndex((tab) => tab.eventId === action.payload);
      if (eventExists === -1) {
        const event = state.events.find((event) => event.id === action.payload);
        if (event) {
          state.tabs.unshift({
            eventId: event.id,
            eventNo: event.no,
            sportAlias: event.alias,
          });
        }
      }
    },
    deleteLiveEventTab: (state, action: PayloadAction<number>) => {
      const index = state.tabs.findIndex((tab) => tab.eventId === action.payload);
      if (index !== -1) {
        state.tabs.splice(index, 1);
      }
    },
    livePosition: (state, action: PayloadAction<any>) => {
      if (action.payload.screens !== undefined && Object.keys(action.payload.screens).length > 0) {
        state.events.map((item, key) => {
          if (action.payload.screens[item.id] !== undefined) {
            state.events[key].position2 = action.payload.screens[item.id].online;
          }
          return null;
        });
        state.events = sortLiveEvents(state.events);
      }
    },
    liveTop: (state, action: PayloadAction<{ events: Array<{ eventID: number; bets: number }> }>) => {
      const events = Array.isArray(action.payload.events) ? action.payload.events : [];
      state.topEventIds = events.map((item) => item.eventID);
      events.forEach((item) => {
        const index = state.events.findIndex((event) => event.id === item.eventID);
        if (index !== -1) {
          state.events[index].betsCount = item.bets;
          state.events[index].top = true;
        }
      });
      state.events.forEach((event) => {
        if (!state.topEventIds.includes(event.id)) {
          event.top = false;
        }
      });
    },
    setLiveFavorites: (state, action: PayloadAction<number[]>) => {
      const favorites = action.payload;
      if (Array.isArray(favorites) && favorites.length && state.events.length) {
        const eventIds = state.events.map((event) => event.id);
        state.favorites = favorites.filter((eventId) => eventIds.includes(eventId));
      }
    },
    syncLiveFavorites: (state, action: PayloadAction<number[]>) => {
      const favorites = action.payload;
      const eventIds = state.events.map((event) => event.id);
      state.favorites = eventIds.length ? favorites.filter((eventId) => eventIds.includes(eventId)) : favorites;
      state.favoritesSyncedAt = Date.now();
    },
    showLiveFavorites: (state, action: PayloadAction<boolean>) => {
      state.showFavorites = action.payload;
    },
    addEventToFavorites: (state, action: PayloadAction<number>) => {
      const eventId = action.payload;
      // const eventExists = state.events.findIndex((event) => event.id === eventId);
      if (!state.favorites.includes(eventId)) {
        state.favorites.push(eventId);
      }
    },
    removeEventFromFavorites: (state, action: PayloadAction<number>) => {
      const eventId = action.payload;
      const index = state.favorites.findIndex((id) => id === eventId);
      if (index !== -1) {
        state.favorites.splice(index, 1);
      }
    },
    liveSocketOpened: {
      reducer: (state, action: StampedAction<undefined>) => {
        state.sync.openedAt = action.meta.at;
      },
      prepare: () => stamped(undefined),
    },
    liveSocketClosed: (state) => {
      state.sync.openedAt = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(liveApi.endpoints.fetchLiveEvents.matchPending, (state, { meta }) => {
        state.sync.listSince = requestSince(state.sync, meta.startedTimeStamp);
        state.sync.listStartedAt = meta.startedTimeStamp;
      })
      .addMatcher(liveApi.endpoints.fetchLiveEvents.matchRejected, (state) => {
        state.sync.listSince = undefined;
        state.sync.listStartedAt = undefined;
      })
      .addMatcher(liveApi.endpoints.fetchLiveEvents.matchFulfilled, (state, { payload }) => {
        const since = state.sync.listSince;
        const startedAt = state.sync.listStartedAt;
        state.sync.listSince = undefined;
        state.sync.listStartedAt = undefined;
        const incoming = payload.events ?? [];
        if (!incoming.length && state.events.some((event) => event.show)) {
          const { emptyAt } = state.sync;
          if (emptyAt === undefined || startedAt === undefined || startedAt < emptyAt + SNAPSHOT_MAX_AGE_MS) {
            if (emptyAt === undefined) {
              state.sync.emptyAt = startedAt;
            }
            return;
          }
        }
        state.sync.emptyAt = undefined;
        state.events = sortLiveEvents(mergeLiveList(state.events, incoming, state.sync, since));
        pruneLiveSync(state.sync, state.events);

        state.sports = payload.sports;
        state.topEventIds = state.events
          .filter((event) => event.top)
          .sort((a, b) => (b.betsCount ?? 0) - (a.betsCount ?? 0))
          .map((event) => event.id);
      })
      .addMatcher(liveApi.endpoints.fetchLiveEventById.matchPending, (state, { meta }) => {
        state.sync.eventSince[meta.arg.originalArgs] = requestSince(state.sync, meta.startedTimeStamp);
      })
      .addMatcher(liveApi.endpoints.fetchLiveEventById.matchRejected, (state, { meta, payload }) => {
        const eventId = meta.arg.originalArgs;
        const since = state.sync.eventSince[eventId];
        delete state.sync.eventSince[eventId];
        const notLive = (payload as { status?: unknown } | undefined)?.status === 404;
        if (notLive && state.events.some((event) => event.id === eventId) && !isNewer(state.sync.marks[eventId]?.show, since)) {
          hideEvent(state, eventId);
        }
      })
      .addMatcher(liveApi.endpoints.fetchLiveEventById.matchFulfilled, (state, { payload, meta }) => {
        const eventId = meta.arg.originalArgs;
        const since = state.sync.eventSince[eventId];
        delete state.sync.eventSince[eventId];
        const index = state.events.findIndex((event) => event.id === eventId);
        if (payload) {
          if (index === -1) {
            state.events.push(payload);
          } else {
            const prev = state.events[index];
            state.events[index] = mergeLiveEvent(prev, payload, state.sync, since, { ...prev, ...payload });
          }
          state.events = sortLiveEvents(state.events);
        } else if (index !== -1 && !isNewer(state.sync.marks[eventId]?.show, since)) {
          hideEvent(state, eventId);
        }
      });
  },
});

export const {
  setLiveEventRates,
  setLiveEventInfoLine,
  setLiveSports,
  showLiveEvent,
  hideLiveEvent,
  lockLiveEvent,
  unlockLiveEvent,
  lockAllLiveEvent,
  unlockAllLiveEvent,
  lockAllScout,
  addLiveEventTab,
  deleteLiveEventTab,
  setLiveFavorites,
  syncLiveFavorites,
  showLiveFavorites,
  addEventToFavorites,
  removeEventFromFavorites,
  livePosition,
  liveTop,
  liveSocketOpened,
  liveSocketClosed,
} = liveSlice.actions;
export default liveSlice.reducer;
